import type {
  DocumentSearchSourceMetadata,
  ShellFileSourceMetadata,
  AttachedFileSourceMetadata,
} from '@newfront-insurance/ai-api';
import type { UIMessagePart } from 'ai';

import type { UIMessageWithMetadata } from './types';

/**
 * Provider metadata namespace for document search sources
 */
const PROVIDER_METADATA_NAMESPACE = 'newfrontDocumentSearch' as const;

/**
 * Provider metadata namespace for data explorer sources
 */
const DATA_EXPLORER_METADATA_NAMESPACE = 'newfrontDataExplorer' as const;

const ATTACHED_FILE_METADATA_NAMESPACE = 'newfrontAttachedFileMetadata' as const;

/**
 * Provider metadata namespace for shell execution generated file sources
 */
const SHELL_METADATA_NAMESPACE = 'newfrontShell' as const;

// TODO: Replace with types from @newfront-insurance/ai-api once published
export type DataExplorerSourceMetadata = {
  entityType: string;
  preview: Array<{ key: string; value: string }>;
};

export type ShellArtifactKind = ShellFileSourceMetadata['kind'] | 'folder';
export type ShellSourceMetadata = {
  kind: ShellArtifactKind;
  fileUuid: string | null;
  containerId: string | null;
  openaiFileId: string | null;
  containerPath: string;
  pending: boolean;
  failed: boolean;
  failureReason: string | null;
};

export type AttachedSourceMetadata = AttachedFileSourceMetadata;

/**
 * Represents a source part from the AI SDK stream.
 *
 * Note: AI SDK expects providerMetadata to be Record<string, Record<string, JSONValue>>.
 * Our backend wraps our metadata under the "newfrontDocumentSearch", "newfrontDataExplorer",
 * or "newfront" key. Source parts may arrive as either "source-url" or "source-document"
 * depending on the AI provider.
 */
export type SourceUrlPart = {
  type: 'source-url';
  sourceId: string;
  url: string;
  title: string;
  providerMetadata?: {
    [PROVIDER_METADATA_NAMESPACE]?: DocumentSearchSourceMetadata;
    [DATA_EXPLORER_METADATA_NAMESPACE]?: DataExplorerSourceMetadata;
    [SHELL_METADATA_NAMESPACE]?: ShellSourceMetadata;
    [ATTACHED_FILE_METADATA_NAMESPACE]?: AttachedSourceMetadata;
  };
};

/**
 * Represents a source-document part from the AI SDK stream (e.g. from OpenAI container file citations).
 * Unlike source-url, these may not have a URL field.
 */
export type SourceDocumentPart = {
  type: 'source-document';
  sourceId: string;
  title: string;
  filename?: string;
  mediaType: string;
  pending?: boolean;
  providerMetadata?: {
    [PROVIDER_METADATA_NAMESPACE]?: DocumentSearchSourceMetadata;
    [DATA_EXPLORER_METADATA_NAMESPACE]?: DataExplorerSourceMetadata;
    [SHELL_METADATA_NAMESPACE]?: ShellSourceMetadata;
    [ATTACHED_FILE_METADATA_NAMESPACE]?: AttachedSourceMetadata;
  };
};

export type SourcePart = SourceUrlPart | SourceDocumentPart;

/**
 * Map of sourceId to source data
 */
export type SourcesMap = Map<string, SourcePart>;

export type DocumentSearchSourceMetadataWithFileUuid = DocumentSearchSourceMetadata & {
  fileUuid?: string | null;
};

/**
 * Type guard to check if a message part is a source part (source-url or source-document)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isSourcePart(part: UIMessagePart<any, any>): part is SourcePart {
  return (part.type === 'source-url' || part.type === 'source-document') && 'sourceId' in part;
}

/**
 * Extracts all source-url parts from a message without filtering.
 */
export function extractAllSourcesFromMessage(message: UIMessageWithMetadata): SourcesMap {
  const sourcesMap = new Map<string, SourcePart>();

  if (!message.parts) {
    return sourcesMap;
  }

  message.parts.forEach((part) => {
    if (isSourcePart(part)) {
      sourcesMap.set(part.sourceId, part);
    }
  });

  return sourcesMap;
}

/**
 * Helper to safely get document search metadata from a source
 */
export function getSourceMetadata(source: SourcePart): DocumentSearchSourceMetadataWithFileUuid | undefined {
  return source.providerMetadata?.[PROVIDER_METADATA_NAMESPACE] as DocumentSearchSourceMetadataWithFileUuid | undefined;
}

/**
 * Helper to safely get data explorer metadata from a source
 */
export function getDataExplorerSourceMetadata(source: SourcePart): DataExplorerSourceMetadata | undefined {
  return source.providerMetadata?.[DATA_EXPLORER_METADATA_NAMESPACE];
}

export function getAttachedFileSourceMetadata(source: SourcePart): AttachedSourceMetadata | undefined {
  return source.providerMetadata?.[ATTACHED_FILE_METADATA_NAMESPACE];
}
/**
 * Helper to safely get skill script metadata from a source
 */
export function getShellSourceMetadata(source: SourcePart): ShellSourceMetadata | undefined {
  return source.providerMetadata?.[SHELL_METADATA_NAMESPACE];
}

export type SandboxFile = {
  kind: ShellArtifactKind;
  fileUuid: string | null;
  filename: string;
  isPending: boolean;
  failed: boolean;
  failureReason: string | null;
};

function isPendingShellSource(source: SourcePart, metadata: ShellSourceMetadata): boolean {
  if (metadata.failed) {
    return false;
  }

  if ((source as SourceDocumentPart).pending) {
    return true;
  }

  if (metadata.pending) {
    return true;
  }

  return !metadata.fileUuid;
}

/**
 * Builds a map of filename -> file data from sources with shell (newfrontShell) metadata.
 * Used to resolve sandbox: links in markdown to downloadable files.
 * The filename comes from the source-document's top-level `filename` or `title` field,
 * while the fileUuid comes from the shell metadata.
 */
export function buildSandboxFileMap(sourcesMap: SourcesMap): Map<string, SandboxFile> {
  const fileMap = new Map<string, SandboxFile>();

  sourcesMap.forEach((source) => {
    const metadata = getShellSourceMetadata(source);
    if (metadata) {
      const filename = ('filename' in source ? source.filename : undefined) ?? source.title;
      fileMap.set(filename, {
        kind: metadata.kind,
        fileUuid: metadata.fileUuid ?? null,
        filename,
        isPending: isPendingShellSource(source, metadata),
        failed: metadata.failed,
        failureReason: metadata.failureReason,
      });
    }
  });

  return fileMap;
}

/**
 * Pattern to match [SOURCE-<id>] citations in text
 * Also matches an optional preceding backslash (LLMs sometimes escape the bracket as \[SOURCE-x])
 */
const SOURCE_MARKER_PATTERN = /\\?\[SOURCE-([^\]]+)\]/g;

/**
 * Pattern to match <citation sourceid="<id>"> or <citation sourceId="<id>"> tags
 * already present in the text (LLMs sometimes output citation HTML tags directly).
 * Case-insensitive on the attribute name since this matches raw text before HTML parsing.
 */
const CITATION_TAG_PATTERN = /<citation\s+source[Ii]d="([^"]+)"[^>]*>/g;

/**
 * Extracts cited source IDs from message text parts.
 * Returns a Set of sourceIds that are referenced via [SOURCE-xxx] markers
 * or <citation sourceid="xxx"> tags.
 */
function extractCitedSourceIds(message: UIMessageWithMetadata): Set<string> {
  const citedSourceIds = new Set<string>();

  if (!message.parts) {
    return citedSourceIds;
  }

  message.parts.forEach((part) => {
    if (part.type === 'text') {
      const { text } = part;

      for (const match of text.matchAll(SOURCE_MARKER_PATTERN)) {
        citedSourceIds.add(match[1]);
      }

      for (const match of text.matchAll(CITATION_TAG_PATTERN)) {
        citedSourceIds.add(match[1]);
      }
    }
  });

  return citedSourceIds;
}

/**
 * Extracts source-url parts from a message and builds a sources map.
 * Filtering rules:
 * - Sources WITH newfrontDocumentSearch metadata: only included if cited in text
 * - Sources WITHOUT newfrontDocumentSearch metadata: always included
 * The map preserves insertion order, which determines citation numbering.
 */
export function extractSourcesFromMessage(message: UIMessageWithMetadata, fallbackSourcesMap?: SourcesMap): SourcesMap {
  const sourcesMap = new Map<string, SourcePart>();

  if (!message.parts) {
    return sourcesMap;
  }

  // First, find all cited source IDs in the message text
  const citedSourceIds = extractCitedSourceIds(message);

  // Then, collect sources based on their metadata type
  message.parts.forEach((part) => {
    if (isSourcePart(part)) {
      // source-document parts (OpenAI container file citations, shell files) are
      // handled separately — only source-url parts go into the citation/sources panel
      if (part.type === 'source-document') return;

      const hasKnownMetadata =
        !!getSourceMetadata(part) || !!getDataExplorerSourceMetadata(part) || !!getAttachedFileSourceMetadata(part);

      if (hasKnownMetadata) {
        // Only include known-namespace sources if they're cited in the text
        if (citedSourceIds.has(part.sourceId)) {
          sourcesMap.set(part.sourceId, part);
        }
      } else {
        // Always include sources from other namespaces
        sourcesMap.set(part.sourceId, part);
      }
    }
  });

  // Resolve cited sources from prior message history when the current
  // message references [SOURCE-xxx] IDs without including source-url parts.
  citedSourceIds.forEach((sourceId) => {
    if (sourcesMap.has(sourceId)) {
      return;
    }

    const fallbackSource = fallbackSourcesMap?.get(sourceId);
    if (fallbackSource) {
      sourcesMap.set(sourceId, fallbackSource);
    }
  });

  return sourcesMap;
}

/**
 * Checks if a message has any sources that should be displayed.
 * Returns true if there are:
 * - Sources with known Newfront metadata that are cited in text, OR
 * - Sources without known Newfront metadata (always shown)
 * This matches the filtering logic in extractSourcesFromMessage.
 */
export function hasSources(message: UIMessageWithMetadata, fallbackSourcesMap?: SourcesMap): boolean {
  if (!message.parts) {
    return false;
  }

  const citedSourceIds = extractCitedSourceIds(message);

  const hasLocalSources = message.parts.some((part) => {
    if (isSourcePart(part)) {
      // source-document parts are handled separately — only source-url parts count
      if (part.type === 'source-document') return false;

      const hasKnownMetadata =
        !!getSourceMetadata(part) || !!getDataExplorerSourceMetadata(part) || !!getAttachedFileSourceMetadata(part);

      if (hasKnownMetadata) {
        // Known-namespace sources must be cited
        return citedSourceIds.has(part.sourceId);
      }
      // Other sources are always shown
      return true;
    }
    return false;
  });

  if (hasLocalSources) {
    return true;
  }

  if (!fallbackSourcesMap || citedSourceIds.size === 0) {
    return false;
  }

  return Array.from(citedSourceIds).some((sourceId) => fallbackSourcesMap.has(sourceId));
}

/**
 * Builds a map of messageId -> sources that were available BEFORE that message.
 * This allows follow-up messages to resolve citations to previously emitted sources.
 */
export function buildPriorSourcesByMessageId(messages: UIMessageWithMetadata[]): Map<string, SourcesMap> {
  const priorSourcesByMessageId = new Map<string, SourcesMap>();
  const runningSources = new Map<string, SourcePart>();

  messages.forEach((message) => {
    priorSourcesByMessageId.set(message.id, new Map(runningSources));

    const messageSources = extractAllSourcesFromMessage(message);
    messageSources.forEach((source, sourceId) => {
      runningSources.set(sourceId, source);
    });
  });

  return priorSourcesByMessageId;
}

/**
 * Gets an array of sources in insertion order.
 * The array index determines the citation number (1-based).
 */
export function getSourcesArray(sourcesMap: SourcesMap): SourcePart[] {
  return Array.from(sourcesMap.values());
}
